import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './ProfileInfoTab.module.css';
import shared from './Profile.module.css';
import { Button, CopyableValue } from '@/components/ui';
import type { EmgAuthClaims } from '@/auth/authClient';

export interface ProfileInfoTabProps {
    claims: EmgAuthClaims;
    displayName: string;
    /** "role,role · org name" from real `/me/access` data — see ProfilePage.tsx. */
    roleLine?: string;
    onEditProfile: () => void;
    onOpenPasswordModal: () => void;
}

/** A read-only info-grid row — frame 77's `180px label / value` two-column table. */
function InfoRow({ label, children }: { label: string; children: ReactNode }) {
    return (
        <div className={styles.infoRow}>
            <span className={styles.infoLabel}>{label}</span>
            <span className={styles.infoValue}>{children}</span>
        </div>
    );
}

/**
 * `/profile` — "Thông tin" tab (frame 77).
 *
 * EAA-1820: identity header + two read-only cards ("Thông tin cá nhân",
 * "Định danh đăng nhập") + a "Mật khẩu" card. The frame's personal-info card
 * shows phone/birthday/gender/address as real values, but D10 (`emg_shared`
 * kernel purity) pins the identity token to sub/email/name/sid ONLY, and
 * `profileFormState.ts` already established (EAA-1822) that no portal-api
 * model persists any of those fields — so every row this session cannot back
 * with real data renders `profile.infoTab.noData` rather than the frame's
 * literal sample value. Giới tính's amber "Chưa chọn" state is real here
 * (genuinely unset, not fabricated) and happens to match the frame exactly.
 *
 * Same honesty rule applies to "Định danh đăng nhập": `EmgAuthClaims` has no
 * username/realm/created-at field (never has — see `authClient.ts`), so only
 * "Mã sub" is real; "Tên đăng nhập" / "Realm" / "Tạo lúc" render as
 * unavailable rather than parsed out of the JWT behind the kernel's back.
 * "Đổi lần cuối" on the Mật khẩu card is the same gap — Keycloak's token
 * carries no password-changed-at claim.
 */
export function ProfileInfoTab({
    claims,
    displayName,
    roleLine,
    onEditProfile,
    onOpenPasswordModal,
}: ProfileInfoTabProps) {
    const { t } = useTranslation();
    const initial = displayName.trim().charAt(0).toUpperCase() || '?';
    const noData = t('profile.infoTab.noData');

    return (
        <>
            <div className={`${shared.card} ${styles.identityCard}`}>
                <span className={styles.avatar} aria-hidden="true">
                    {initial}
                </span>
                <div className={styles.identityBody}>
                    <span className={styles.identityName}>
                        {claims.name ?? claims.email ?? t('profile.defaultUserName')}
                    </span>
                    {roleLine && <span className={shared.roleLine}>{roleLine}</span>}
                    <span className={styles.statusRow}>
                        <span className={styles.statusDot} aria-hidden="true" />
                        {t('profile.activeStatus')}
                    </span>
                </div>
                {/* EAA-1804: kit `Button` instead of hand-written `<button className="portal-btn">`. */}
                <Button type="primary" onClick={onEditProfile}>
                    {t('profile.editProfile')}
                </Button>
            </div>

            <div className={styles.infoGrid}>
                <div className={`${shared.card} ${shared.cardFlush}`}>
                    <div className={shared.infoCardHeader}>
                        <span className={shared.infoCardHeading}>
                            <span className={shared.infoCardTitle}>
                                {t('profile.infoTab.personalCardTitle')}
                            </span>
                            <span className={shared.infoCardDesc}>
                                {t('profile.infoTab.personalCardDesc')}
                            </span>
                        </span>
                        <Button onClick={onEditProfile}>
                            {t('profile.infoTab.editAction')}
                        </Button>
                    </div>
                    <div className={styles.infoTable}>
                        <InfoRow label={t('profile.infoTab.rowFullName')}>{displayName}</InfoRow>
                        <InfoRow label={t('profile.infoTab.rowEmail')}>
                            {claims.email ?? noData}
                            <span className={styles.infoHint}>
                                {t('profile.infoTab.rowEmailHelper')}
                            </span>
                        </InfoRow>
                        <InfoRow label={t('profile.infoTab.rowPhone')}>{noData}</InfoRow>
                        <InfoRow label={t('profile.infoTab.rowBirthday')}>{noData}</InfoRow>
                        <InfoRow label={t('profile.infoTab.rowGender')}>
                            <span className={styles.infoWarn}>
                                {t('profile.infoTab.rowGenderUnset')}
                            </span>
                            <span className={styles.infoHint}>
                                {t('profile.infoTab.rowGenderHelper')}
                            </span>
                        </InfoRow>
                        <InfoRow label={t('profile.infoTab.rowAddress')}>{noData}</InfoRow>
                    </div>
                </div>

                <div className={`${shared.card} ${shared.cardFlush}`}>
                    <div className={shared.infoCardHeader}>
                        <span className={shared.infoCardHeading}>
                            <span className={shared.infoCardTitle}>
                                {t('profile.infoTab.identityCardTitle')}
                            </span>
                            <span className={shared.infoCardDesc}>
                                {t('profile.infoTab.identityCardDesc')}
                            </span>
                        </span>
                    </div>
                    <div className={styles.infoTable}>
                        <InfoRow label={t('profile.infoTab.rowUsername')}>{noData}</InfoRow>
                        <InfoRow label={t('profile.infoTab.rowSub')}>
                            {/* EAA-1804: app-local Profile/CopyableValue.tsx deleted — it was
                                one of the two hand-rolled originals named in
                                packages/ui/src/components/CopyableValue/CopyableValue.tsx's
                                own doc comment (the exact "hand-rolled twice" gap the kit
                                exists to close). Same `value`/`ariaLabel` props, same
                                copy-then-checkmark-for-1.5s behaviour; the kit version also
                                clears its timeout on unmount, which the app-local one did not. */}
                            <CopyableValue value={claims.sub} ariaLabel={t('profile.subLabel')} />
                        </InfoRow>
                        <InfoRow label={t('profile.infoTab.rowRealm')}>{noData}</InfoRow>
                        <InfoRow label={t('profile.infoTab.rowCreatedAt')}>{noData}</InfoRow>
                    </div>
                </div>
            </div>

            <div className={`${shared.card} ${shared.passwordCard}`}>
                <div className={shared.infoCardHeading}>
                    <span className={shared.infoCardTitle}>
                        {t('profile.infoTab.passwordCardTitle')}
                    </span>
                    <span className={shared.infoCardDesc}>
                        {t('profile.infoTab.passwordCardDesc')}
                    </span>
                </div>
                <div className={styles.passwordCardRow}>
                    <span className={styles.passwordLastChanged}>
                        {t('profile.infoTab.passwordLastChangedUnknown')}
                    </span>
                    <Button onClick={onOpenPasswordModal}>{t('profile.changePassword')}</Button>
                </div>
            </div>
        </>
    );
}

export default ProfileInfoTab;
